import { Create, Delete } from '@material-ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableCellProps } from 'react-virtualized';

import { useOrganizationUsers } from 'api';
import {
  useDeleteTaskDefinition,
  useTaskDefinitions,
} from 'api/organizations/plugins/reconvelocity/tasks';
import useSession from 'api/session';
import permissions from 'common/permissions';
import PermissionGate from 'components/PermissionGate';
import Alert, { useAlert } from 'components/shared/Alert';
import CollectionHeaderBar from 'components/shared/CollectionHeaderBar';
import ConfirmationDialog, {
  useConfirmationDialog,
} from 'components/shared/ConfirmationDialog';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import Table from 'components/shared/Table';
import { Organization, User } from 'models';
import { ReconVelocityTaskDefinition } from 'models/plugins/reconVelocityPlugin';

import EditTaskTemplateDialog from './EditTaskTemplateDialog';

import './TasksView.scss';

interface TasksViewProps {
  orgId: Organization['id'];
  fetchOnMount: boolean;
}

var TasksView = ({ orgId, fetchOnMount }: TasksViewProps) => {
  const {
    data: taskDefinitions,
    isLoading: taskDefinitionsIsLoading,
    isError: taskDefinitionsError,
  } = useTaskDefinitions(orgId, { enabled: fetchOnMount });
  const { deleteTaskDefinitionAsync } = useDeleteTaskDefinition(orgId);
  const {
    data: organizationUsersData,
    isLoading: organizationUsersAreLoading,
    isError: organizationUsersError,
  } = useOrganizationUsers(orgId);
  const organizationUsersDataPages = organizationUsersData?.pages;
  const organizationUsers = useMemo(
    () =>
      organizationUsersDataPages?.reduce(
        (acc: User[], pageUsers) => [...acc, ...pageUsers.data],
        []
      ) ?? [],
    [organizationUsersDataPages]
  );
  const {
    data: session,
    isLoading: sessionIsLoading,
    isError: sessionError,
  } = useSession();

  const { isAlertOpen, alertMessage, openAlert, closeAlert, variant } =
    useAlert();
  const {
    isConfirmationDialogOpen,
    isConfirmationLoading,
    onAccept,
    onDeny,
    confirmationMessage,
    openConfirmationDialog,
  } = useConfirmationDialog();

  const [editingTaskDefinition, setEditingTaskDefinition] =
    useState<ReconVelocityTaskDefinition>();

  const isLoading =
    taskDefinitionsIsLoading || organizationUsersAreLoading || sessionIsLoading;

  useEffect(() => {
    if (taskDefinitionsError || organizationUsersError || sessionError) {
      openAlert('There was an API error loading the task definitions', 'error');
    }
  }, [taskDefinitionsError, organizationUsersError, sessionError, openAlert]);

  function openEditTaskTemplateDialog(
    taskDefinition?: ReconVelocityTaskDefinition
  ) {
    setEditingTaskDefinition({
      title: '',
      description: '',
      requiresApproval: true,
      addByDefaultForNewRecon: false,
      defaultAssigneeId: null,
      defaultPartsCost: {
        amount: 0,
        currency: 'USD',
      },
      defaultLaborRate: {
        amount: 0,
        currency: 'USD',
      },
      defaultLaborCost: {
        amount: 0,
        currency: 'USD',
      },
      defaultLaborHours: 0,
      tags: [],

      ...(taskDefinition ?? {}),
    });
  }

  const handleDeleteClick = useCallback(
    (taskDefinition: ReconVelocityTaskDefinition) => {
      openConfirmationDialog({
        primaryButtonLabelOverride: 'Remove',
        onAcceptOverride: async () => {
          await deleteTaskDefinitionAsync(taskDefinition.id);
        },
        messageOverride: `Remove task template ${taskDefinition.title}?`,
      });
    },
    [deleteTaskDefinitionAsync, openConfirmationDialog]
  );

  const handleAdd = () => openEditTaskTemplateDialog();

  const closeEditTaskTemplateDialog = () => setEditingTaskDefinition(undefined);

  const defaultLaborRateAmount = 0;

  const totalLaborPriceAmount = (
    taskDefinition: ReconVelocityTaskDefinition
  ) => {
    if (taskDefinition.defaultLaborCost?.amount)
      return taskDefinition.defaultLaborCost.amount;
    return (
      taskDefinition.defaultLaborHours *
      (taskDefinition.defaultLaborRate?.amount ?? defaultLaborRateAmount)
    );
  };

  const totalPriceAmount = useCallback(
    (taskDefinition: ReconVelocityTaskDefinition) =>
      totalLaborPriceAmount(taskDefinition) +
      (taskDefinition.defaultPartsCost?.amount ?? 0),
    []
  );

  const hasAddTaskPermission = session?.permissions.includes(
    permissions.ORGS_PLUGINS_RECONVELOCITY_TASK_DEFINITIONS_CREATE
  );

  const taskDefinitionsData = taskDefinitions?.data;

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="TasksView_container" id="tasks-overview-container">
      <CollectionHeaderBar
        count={taskDefinitionsData?.length ?? 0}
        unit="Task Template"
        onAdd={hasAddTaskPermission ? handleAdd : undefined}
      />
      <Table
        items={taskDefinitionsData}
        columns={[
          {
            heading: 'Task',
            dataKey: 'title',
            flexGrow: 2,
          },
          {
            heading: 'Description',
            dataKey: 'description',
            flexGrow: 2,
          },
          {
            heading: 'Default Assignee',
            content: ({
              rowData: taskDefinition,
            }: {
              rowData: ReconVelocityTaskDefinition;
            }) => {
              if (
                taskDefinition.defaultAssigneeId &&
                organizationUsersAreLoading
              ) {
                return <LoadingIndicator size={15} color="secondary" />;
              }

              const defaultUser = organizationUsers.find(
                ({ id }) => id === taskDefinition.defaultAssigneeId
              );

              if (!defaultUser) return null;

              return (
                <span>
                  {defaultUser.firstName} {defaultUser.lastName}
                </span>
              );
            },
            flexGrow: 0.5,
          },
          {
            heading: 'Labor Type',
            dataKey: 'taskDefinition.defaultLaborRate.amount',
            value: ({ rowData: taskDefinition }) =>
              taskDefinition?.defaultLaborRate?.amount ? 'HOURLY' : 'FIXED',
            width: 80,
          },
          {
            heading: 'Labor',
            dataKey: 'labor',
            value: ({ rowData: taskDefinition }) =>
              totalLaborPriceAmount(taskDefinition),
            fixedDecimalScale: true,
            decimalScale: 2,
            thousandSeparator: true,
            className: 'TasksView_labor',
            headerClassName: 'TasksView_labor',
            width: 70,
          },
          {
            heading: 'Parts',
            dataKey: 'defaultPartsCost.amount',
            fixedDecimalScale: true,
            decimalScale: 2,
            thousandSeparator: true,
            className: 'TasksView_parts',
            headerClassName: 'TasksView_parts',
            width: 70,
          },
          {
            heading: 'Total',
            dataKey: 'total',
            value: ({ rowData: taskDefinition }) =>
              totalPriceAmount(taskDefinition),
            fixedDecimalScale: true,
            decimalScale: 2,
            thousandSeparator: true,
            className: 'TasksView_total',
            headerClassName: 'TasksView_total',
            width: 70,
          },
          {
            heading: '',
            dataKey: 'actions',
            content: ({ rowData: taskDefinition }: TableCellProps) => (
              <div className="TasksView_actions">
                <PermissionGate
                  permissions={[
                    permissions.ORGS_PLUGINS_RECONVELOCITY_TASK_DEFINITIONS_UPDATE,
                  ]}
                >
                  <Create
                    id="edit-tasks-button"
                    onClick={() => openEditTaskTemplateDialog(taskDefinition)}
                  />
                </PermissionGate>
                <PermissionGate
                  permissions={[
                    permissions.ORGS_PLUGINS_RECONVELOCITY_TASK_DEFINITIONS_DELETE,
                  ]}
                >
                  <Delete
                    id="delete-tasks-button"
                    onClick={() => handleDeleteClick(taskDefinition)}
                  />
                </PermissionGate>
              </div>
            ),
            width: 50,
          },
        ]}
      />
      {editingTaskDefinition && (
        <EditTaskTemplateDialog
          orgId={orgId}
          taskDefinition={editingTaskDefinition}
          onClose={closeEditTaskTemplateDialog}
        />
      )}
      <Alert
        open={isAlertOpen}
        message={alertMessage}
        onClose={closeAlert}
        duration={3500}
        variant={variant}
      />
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        isLoading={isConfirmationLoading}
        onPrimaryButtonClick={onAccept}
        onSecondaryButtonClick={onDeny}
        message={confirmationMessage}
      />
    </div>
  );
};

export default TasksView;
